<script lang="ts">
    import { computed, onMounted, onUnmounted, Ref, ref, toRaw } from 'vue';

    import vm, { JourneyLine } from '@journeys/Journeys.viewmodel';

    // directives
    import { vTooltip } from '@shared/directives';

    // components
    import FluidButton from '@cwd/shared/components/FluidButton.vue';
    import ContainerFloating from '@shared/components/ContainerFloating.vue';

    // utils
    import { calc_snap, requestFrame } from '@shared/utils';

    // assets
    import { AlignCenterSVG, DistributeSpaceSVG, ArrowLeftSVG, ArrowRightSVG } from '@shared/assets/vector/ui/IconsUI';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'SelectionContextMenu',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { activeJourney, editor, settings } = vm;

    const selectionArray = computed(() => {
        let items = {
            nodes: Object.values(editor.tools.sel_nodes),
            points: Object.values<any>(editor.tools.sel_points),
            lines: Object.values<JourneyLine>(editor.tools.sel_lines),
        };

        editor.tools.sel_count = items.nodes.length + items.lines.length + items.points.length;
        return items;
    });

    const handleAlign = (axis: 'x' | 'y') => {
        let dim: 'h' | 'w' = axis === 'y' ? 'h' : 'w',
            key_value = editor.tools.sel_keyObj
                ? editor.tools.sel_nodes[editor.tools.sel_keyObj][axis] + editor.tools.sel_nodes[editor.tools.sel_keyObj][dim] / 2
                : editor.tools.sel_dims[axis === 'y' ? 't' : 'l'] + editor.tools.sel_dims[dim] / 2;

        if (settings.grid.snap) {
            key_value = calc_snap({ x: key_value, y: 0 }, settings.grid.spacing).x;
        }

        for (let key in editor.tools.sel_nodes) {
            editor.tools.sel_nodes[key][axis] = key_value - editor.tools.sel_nodes[key][dim] / 2;
        }

        let handledLines: Indexable<string, boolean> = {};
        for (let key in editor.tools.sel_lines) {
            let line: JourneyLine = activeJourney.lines[editor.tools.sel_lines[key].id];

            handledLines[key] = true;

            line[axis] = key_value;
            line[('e' + axis) as 'x'] = key_value;

            let points = line.points;

            if (points.length > 0) for (let point of points) point[axis] = key_value;
        }

        for (let key in editor.tools.sel_points) {
            let point = editor.tools.sel_points[key],
                line: JourneyLine = activeJourney.lines[point.id];

            // if (handledLines[line.id])
            //    return

            if (typeof point.idx === 'string') {
                if (point.idx === 'fr') line[axis] = point[axis] = key_value;
                else line[('e' + axis) as 'x'] = point[axis] = key_value;
            } else if (typeof point.idx === 'number') line.points[point.idx][axis] = point[axis] = key_value;
        }

        requestFrame(() => {
            for (let key in activeJourney.lines) {
                editor.events.emit('newlinehitboxes', activeJourney.lines[key]);
            }

            editor.events.emit('calcselarea', {
                nodes: editor.tools.sel_nodes,
                points: editor.tools.sel_points,
                lines: editor.tools.sel_lines,
            });
        });
    };

    const handleDistribute = (axis: 'x' | 'y') => {
        let spreadMargin = editor.tools.spread_margin,
            axisDim = axis === 'x' ? 'w' : 'h',
            selAreaPadding = editor.tools.sel_areaPadding,
            selectedItems: any = Object.values(selectionArray.value.nodes),
            handledLines: Indexable<string, boolean> = {};

        for (let key in selectionArray.value.lines) {
            let line = selectionArray.value.lines[key];

            if (line.points.length)
                line.points.forEach(({ x, y }: any, idx: number) => {
                    selectedItems.push({ id: line.id, idx, x, y });
                });

            if (!line.to) selectedItems.push({ id: line.id, idx: 'to', x: line.ex, y: line.ey });

            if (!line.fr) selectedItems.push({ id: line.id, idx: 'fr', x: line.x, y: line.y });

            handledLines[line.id] = true;
        }

        for (let key in selectionArray.value.points) {
            let point = selectionArray.value.points[key];

            if (handledLines[point.id]) continue;

            selectedItems.push(point);
        }

        selectedItems.sort((a: any, b: any) => a[axis] + (a[axisDim] ?? 0) / 2 - (b[axis] + (b[axisDim] ?? 0) / 2));

        let key_idx = -1;
        if (editor.tools.sel_keyObj)
            for (let i = 0; i < selectedItems.length; i++)
                if (selectedItems[i].id === editor.tools.sel_keyObj) {
                    key_idx = i;
                    break;
                }

        let snap = settings.grid.snap,
            spacing = settings.grid.spacing,
            placementStart = selectedItems[key_idx + 1 && key_idx][axis] + (selectedItems[key_idx + 1 && key_idx][axisDim] ?? 0) / 2,
            placementEnd = selectedItems[selectedItems.length - 1][axis] + (selectedItems[selectedItems.length - 1][axisDim] ?? 0) / 2,
            totalItemSize = selectionArray.value.nodes.reduce((acc: number, node: any, idx: number, arr: any) => acc + node[axisDim], 0),
            maxSpace = editor.tools.sel_dims[axisDim as 'x'] - totalItemSize,
            stepSize = key_idx !== -1 ? spreadMargin : maxSpace / (selectedItems.length - 1);

        const cs = (val: number | number[], spacing: number, d?: number | number[]): typeof val => {
            let result: number | number[] = [];

            const calc = (v: number, d: number = 0, resultIsArray?: boolean) => {
                v += d / 2;

                let remainder = v % spacing;

                if (Math.abs(remainder) > spacing / 2) v += spacing * (remainder < 0 ? -1 : 1);

                v -= remainder + d / 2;

                if (resultIsArray) Array.isArray(result) && result.push(v);
                else result = v;
            };

            if (Array.isArray(val)) for (let i = 0; i < val.length; i++) calc(val[i], Array.isArray(d) ? d[i] : d, true);
            else calc(val, d as number);

            return result;
        };

        if (snap) {
            maxSpace -= maxSpace % spacing;
            stepSize -= stepSize % spacing;
            placementStart = cs(placementStart, spacing);
        }

        let lastPos = placementStart;
        for (let i = 0; i < selectedItems.length; i++) {
            let ci = i,
                mod: number,
                position: number;

            // if key_obj is selected spread by margin
            if (key_idx !== -1) {
                if (ci < key_idx) {
                    // rtl from key
                    ci = key_idx - i - 1;

                    mod = (stepSize + (selectedItems[ci + 1][axisDim] ?? 0) / 2 + (selectedItems[ci][axisDim] ?? 0) / 2) * -1;
                    position = lastPos + (ci !== key_idx ? cs(mod, spacing) : 0);
                } else {
                    // ltr fr key
                    if (ci === key_idx) lastPos = placementStart;

                    mod = ci && stepSize + (selectedItems[ci - 1][axisDim] ?? 0) / 2 + (selectedItems[ci][axisDim] ?? 0) / 2;
                    position = lastPos + (ci !== key_idx ? cs(mod, spacing) : 0);
                }
            } else {
                mod = ci && stepSize + (selectedItems[ci - 1][axisDim] ?? 0) / 2 + (selectedItems[ci][axisDim] ?? 0) / 2;
                position = lastPos + (snap ? cs(mod, spacing) : ~~(mod + 0.5));
            }

            lastPos = position;

            if (typeof selectedItems[ci].idx === 'string') {
                let point = editor.tools.sel_points[`l${selectedItems[ci].id}${selectedItems[ci].idx}`];

                activeJourney.lines[selectedItems[ci].id][`${selectedItems[ci].idx === 'to' ? 'e' : ''}${axis}`] = position;

                if (point) point[axis] = position;
            } else if (typeof selectedItems[ci].idx === 'number') {
                let point = editor.tools.sel_points[`l${selectedItems[ci].id}p${selectedItems[ci].idx}`];

                activeJourney.lines[selectedItems[ci].id].points[selectedItems[ci].idx][axis] = position;

                if (point) point[axis] = position;
            } else selectedItems[ci][axis] = position - selectedItems[ci][axisDim] / 2;
        }

        requestFrame(() => {
            for (let key in activeJourney.lines) {
                editor.events.emit('newlinehitboxes', activeJourney.lines[key]);
            }

            editor.events.emit('calcselarea', {
                nodes: editor.tools.sel_nodes,
                points: editor.tools.sel_points,
                lines: editor.tools.sel_lines,
            });
        });
    };

    const adjustSpreadMargin = (mod: number) => {
        if (mod === -1 && editor.tools.spread_margin <= 0) return;

        editor.tools.spread_margin += settings.grid.spacing * mod;
    };
</script>

<template>
    <ContainerFloating class="horizontal canvas-menu">
        <FluidButton
            :icon="AlignCenterSVG"
            @click="() => handleAlign('x')"
            class="btn btn-default"
            :class="editor.tools.sel_count < 2 ? 'inactive' : ''"
            v-tooltip.right="'Align Horizontal'" />
        <FluidButton
            :icon="AlignCenterSVG"
            iconClasses="rotate-90"
            @click="() => handleAlign('y')"
            class="btn btn-default"
            :class="editor.tools.sel_count < 2 ? 'inactive' : ''"
            v-tooltip.right="'Align Vertical'" />

        <template v-if="editor.tools.sel_count > 2">
            <div class="separator vertical"></div>

            <FluidButton class="btn btn-default" :icon="DistributeSpaceSVG" @click="() => handleDistribute('y')" v-tooltip.right="'Spread vertically'" />
            <FluidButton
                class="btn btn-default"
                :icon="DistributeSpaceSVG"
                iconClasses="rotate-90"
                @click="() => handleDistribute('x')"
                v-tooltip.right="'Spread horizontally'" />

            <div class="settings-group horizontal fl" v-if="editor.tools.sel_keyObj">
                <ArrowLeftSVG class="control-arrow" @click="adjustSpreadMargin(-1)" />
                <h3>{{ editor.tools.spread_margin }}</h3>
                <ArrowRightSVG class="control-arrow" @click="adjustSpreadMargin(+1)" />
            </div>
        </template>
    </ContainerFloating>
</template>

<style>
    .canvas-menu {
        --zscale: 1;
        transform: translate3d(-50%, 0, 0) scale(var(--zscale));
        transform-origin: bottom;
        transition-property: transform, top, left;
        transition-duration: var(--td-fast);
        transition-timing-function: ease-out;
    }
</style>
